/**
 * Dependencies.
 */
import { BrowserMultiFormatReader } from '@zxing/library';
import { debounce, orderBy, uniqBy } from 'lodash-es';
import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';

import PackageModel from '@/api/package/package.model';
import { servicePointOrderPackageService } from '@/api/service-point/order-package/order-package.service';
import { ServicePointOrderModel } from '@/api/service-point/order/order.model';
import { servicePointOrderService } from '@/api/service-point/order/order.service';
import OrderSteps from '@/modules/service-point/components/order/order-steps/order-steps.component.vue';
import orderState from '@/modules/service-point/constants/order.constant';
import { servicePointStore } from '@/modules/service-point/store';

/**
 * Component.
 */
@Component({
  components: { 'order-steps': OrderSteps },
  name: 'deliver-order-scan-scene'
})
export default class extends Vue {
  /**
   * Property `orderState`.
   */
  orderState = orderState;

  /**
   * Property `isLoadingOrder`.
   */
  isLoadingOrder = false;

  /**
   * Property `isCameraLoading`.
   */
  isCameraLoading = true;

  /**
   * Property `isLoadingPackages`.
   */
  isLoadingPackages = false;

  /**
   * Property `canShowScanCodeModal`.
   */
  canShowScanCodeModal = false;

  /**
   * Property `hasValidTrackingNumberKey`.
   */
  hasValidTrackingNumberKey = false;

  /**
   * Property `pickUpPin`.
   */
  pickUpPin: string = null;

  /**
   * Property `trackingNumberKey`.
   */
  trackingNumberKey: string = null;

  /**
   * Property `order`.
   */
  order: ServicePointOrderModel = null;

  /**
   * Property `orderPackages`.
   */
  orderPackages: PackageModel[] = [];

  /**
   * Property `packagesRead`.
   */
  packagesRead: string[] = [];

  /**
   * Property `isValidatePackagesRequired`.
   */
  isValidatePackagesRequired = false;

  /**
   * Property `searchOrder`.
   */
  searchOrder = debounce(this.handleGetOrderPackages, 250, {
    leading: true,
    trailing: false
  });

  /**
   * Property `searchPackageCode`.
   */
  searchPackageCode = debounce(this.handleValidateCode, 250, {
    leading: true,
    trailing: false
  });

  /**
   * Property `packageCode`.
   */
  packageCode: string = null;

  /**
   * Property `codeReader`.
   */
  codeReader = new BrowserMultiFormatReader();

  /**
   * Getter `packages`.
   */
  get packages() {
    let packages = this.orderPackages;

    packages = uniqBy(packages, 'id');
    packages = orderBy(packages, ['status.code'], ['desc']);

    return packages;
  }

  /**
   * Getter `isAllPackagesDelivered`.
   */
  get isAllPackagesDelivered() {
    let isAllRead = this.packages.length ? true : false;

    this.packages.forEach(
      ({ deliveryPackage: { packageCode = null } = {} }) => {
        if (!this.packagesRead.includes(packageCode)) {
          isAllRead = false;
        }
      }
    );

    return isAllRead;
  }

  /**
   * Getter `totalUndeliveredPackages`.
   */
  get totalUndeliveredPackages() {
    let total = 0;

    this.packages.forEach(
      ({ deliveryPackage: { packageCode = null } = {} }) => {
        if (!this.packagesRead.includes(packageCode)) {
          total++;
        }
      }
    );

    return total;
  }

  /**
   * Getter `totalSteps`.
   */
  get totalSteps() {
    return this.isValidatePackagesRequired ? 3 : 2;
  }

  /**
   * Getter `step`.
   */
  get step() {
    return this.hasValidTrackingNumberKey && this.isValidatePackagesRequired
      ? 2
      : 1;
  }

  /**
   * Getter `notifySucessSettings`.
   */
  get notifySucessSettings() {
    return {
      backgroundColor: 'var(--pk-color-success-lite)',
      displayMode: 2,
      icon: 'material-icons',
      iconColor: 'var(--pk-color-success)',
      iconText: 'check_circle',
      id: 'deliver-success',
      progressBarColor: 'var(--pk-color-success)',
      transitionIn: 'fadeIn',
      transitionInMobile: 'fadeIn',
      transitionOut: 'fadeOut',
      transitionOutMobile: 'fadeOut'
    } as unknown;
  }

  /**
   * Getter `notifyErrorSettings`.
   */
  get notifyErrorSettings() {
    return {
      backgroundColor: 'var(--pk-color-error-lite)',
      displayMode: 2,
      icon: 'material-icons',
      iconColor: 'var(--pk-color-error)',
      iconText: 'warning',
      id: 'deviver-error',
      progressBarColor: 'var(--pk-color-error)',
      transitionIn: 'fadeIn',
      transitionInMobile: 'fadeIn',
      transitionOut: 'fadeOut',
      transitionOutMobile: 'fadeOut'
    } as unknown;
  }

  /**
   * Getter `trackingNumber`.
   */
  get trackingNumber() {
    const { order } = this || {};
    const { delivery } = order || {};
    const { trackingNumber = null } = delivery || {};

    return trackingNumber;
  }

  /**
   * Getter `currentTrackingNumberKey`.
   */
  get currentTrackingNumberKey() {
    const trackingNumberKey = servicePointStore.deliverTrackingNumberKey;

    return trackingNumberKey;
  }

  /**
   * Lifecycle mounted hook.
   */
  mounted() {
    const { currentTrackingNumberKey } = this || {};

    if (currentTrackingNumberKey && this.isValidatePackagesRequired) {
      this.trackingNumberKey = currentTrackingNumberKey;

      this.searchOrder(this.trackingNumberKey, 'key');
    } else {
      servicePointStore.setDeliverTrackingNumberKey(null);
      servicePointStore.setOrder(null);
    }
  }

  /**
   * Lifecycle before destroyed hook.
   */
  beforeDestroy() {
    if (this.codeReader) {
      this.codeReader.reset();
    }
  }

  /**
   * Handle get order packages.
   */
  async handleGetOrderPackages(pickUpCode: string, type: 'key' | 'pin') {
    if (!!pickUpCode && !this.isLoadingOrder) {
      this.isLoadingOrder = true;

      try {
        let order = null;

        if (type === 'key') {
          order = await servicePointOrderService.getByKey(pickUpCode);
        } else {
          order = await servicePointOrderService.getByPin(pickUpCode);
        }

        if (order?.status?.code === this.orderState.RECEIVED) {
          this.order = { ...order };

          const { packages = [], trackingNumberKey = null } = this.order || {};

          this.trackingNumberKey =
            type === 'key' ? pickUpCode : trackingNumberKey;

          if (this.isAllPackagesDelivered) {
            this.handleHideScanCodeModal();
          }

          this.orderPackages = packages;
          this.hasValidTrackingNumberKey = true;

          const { currentTrackingNumberKey } = this || {};
          if (currentTrackingNumberKey) {
            packages.forEach(item => {
              const { deliveryPackage = null } = item || {};
              const { packageCode = null } = deliveryPackage || {};

              if (!this.packagesRead.includes(packageCode)) {
                this.packagesRead.push(packageCode);
              }
            });
          } else {
            this.$kit.notify.show(
              this.i18n('SERVICE_POINT.LABELS.SUCCESS'),
              this.i18n(
                'SERVICE_POINT.SUCCESS.CODE_READ',
                false,
                'upperFirst',
                {
                  code: pickUpCode
                }
              ),
              this.notifySucessSettings
            );
          }

          if (!this.isValidatePackagesRequired) {
            this.handleGoToConfirmDeliver();
          }
        } else {
          throw new Error('Collect with invalid state');
        }
      } catch (error) {
        const { raw } = error || {};
        const { code } = raw || {};

        if (!this.hasValidTrackingNumberKey) {
          switch (code) {
            case 3413:
              this.$kit.notify.show(
                this.i18n('SERVICE_POINT.LABELS.ERROR'),
                this.i18n('SERVICE_POINT.ERRORS.ORDER_DELIVERED'),
                this.notifyErrorSettings
              );
              break;

            default:
              this.$kit.notify.show(
                this.i18n('SERVICE_POINT.LABELS.ERROR'),
                this.i18n(
                  'SERVICE_POINT.ERRORS.VALIDATING_CODE',
                  false,
                  'upperFirst',
                  {
                    code: pickUpCode
                  }
                ),
                this.notifyErrorSettings
              );
              break;
          }
        }

        this.pickUpPin = null;

        if (process.env.BUILD_MODE !== 'prd') {
          console.error({
            error,
            method: 'handleGetOrderPackages',
            scene: 'DeliverOrderScanScene'
          });
        }
      } finally {
        this.isLoadingOrder = false;
      }
    }
  }

  /**
   * Handle copy package code.
   */
  handleCopyPackageCode(code: string) {
    this.packageCode = code;

    this.searchPackageCode(code);
  }

  /**
   * Handle hide scan code modal.
   */
  handleHideScanCodeModal() {
    this.canShowScanCodeModal = false;
  }

  /**
   * Handle show scan code modal.
   */
  handleShowScanCodeModal() {
    this.isCameraLoading = true;
    this.canShowScanCodeModal = true;

    const { canEnumerateDevices = false } = this.codeReader || {};

    if (canEnumerateDevices) {
      this.$nextTick().then(async () => {
        (this.$refs.codeScanner as HTMLVideoElement).oncanplay = () => {
          this.isCameraLoading = false;
        };

        this.handleStartVideoDecoding();
      });
    }
  }

  /**
   * Handle start video decoding.
   */
  async handleStartVideoDecoding() {
    try {
      if (this.codeReader) {
        const result = await this.codeReader.decodeOnceFromVideoDevice(
          undefined,
          this.$refs.codeScanner as HTMLVideoElement
        );

        if (result && result.getText()) {
          const code = result.getText();

          if (!this.hasValidTrackingNumberKey) {
            await this.handleGetOrderPackages(code, 'key');

            this.handleHideScanCodeModal();
          } else {
            await this.handleValidateCode(code);

            this.handleStartVideoDecoding();
          }

          this.codeReader.reset();
        }
      }
    } catch (error) {
      if (process.env.BUILD_MODE !== 'prd') {
        console.error({
          error,
          method: 'handleStartVideoDecoding',
          scene: 'DeliverOrderScanScene'
        });
      }
    }
  }

  /**
   * Handle validate code.
   */
  async handleValidateCode(code: string) {
    const packageCode = code;

    if (!!packageCode && !this.isLoadingPackages) {
      try {
        this.isLoadingPackages = true;

        await servicePointOrderPackageService.receive(packageCode);

        if (!this.packagesRead.includes(code)) {
          this.packagesRead.push(code);
        }

        if (this.isAllPackagesDelivered) {
          this.handleHideScanCodeModal();
        }

        this.$kit.notify.show(
          this.i18n('SERVICE_POINT.LABELS.SUCCESS'),
          this.i18n('SERVICE_POINT.SUCCESS.CODE_READ', false, 'upperFirst', {
            code
          }),
          this.notifySucessSettings
        );

        this.packageCode = null;
      } catch (error) {
        this.$kit.notify.show(
          this.i18n('SERVICE_POINT.LABELS.ERROR'),
          this.i18n('SERVICE_POINT.ERRORS.VALIDATING_CODE'),
          this.notifyErrorSettings
        );

        if (process.env.BUILD_MODE !== 'prd') {
          console.error({
            error,
            method: 'handleValidateCode',
            scene: 'DeliverOrderScanScene'
          });
        }
      } finally {
        this.isLoadingPackages = false;
      }
    }
  }

  /**
   * Handle go to confirm deliver.
   */
  handleGoToConfirmDeliver() {
    servicePointStore.setDeliverTrackingNumberKey(this.trackingNumberKey);
    servicePointStore.setOrder(this.order);

    this.$router.push({
      name: 'ServicePointDeliverOrderConfirm',
      params: {
        totalSteps: String(this.totalSteps)
      }
    });
  }

  /**
   * Handle cancel deliver.
   */
  handleCancelDeliver() {
    this.$router.push({
      name: 'ServicePointHome'
    });
  }

  /**
   * Handle deliver without pin.
   */
  handleDeliverWithoutPin() {
    this.$router.push({
      name: 'ServicePointDeliverOrderWithoutPinList'
    });
  }

  /**
   * Handle change can show scan code modal.
   */
  @Watch('canShowScanCodeModal')
  handleChangeCanShowScanCodeModal() {
    if (this.canShowScanCodeModal === false && this.codeReader) {
      this.codeReader.reset();
    }
  }
}
